import { useContext, useRef, useCallback, useEffect } from "react";
import { ConfigContext } from "../context/ConfigContext";
import { PriceDispatch } from "../context/PriceContext";
import { TextSectionsListContext } from "../context/TextSectionsListContext";
import priceService from "../services/PriceService";

export function usePriceCalculation() {
    const configContext = useContext(ConfigContext);
    const priceDispatch = useContext(PriceDispatch);
    const textSections = useContext(TextSectionsListContext);

    // Keep latest values in refs so the debounced callback always reads fresh data
    const textSectionsRef = useRef(textSections);
    const configContextRef = useRef(configContext);
    useEffect(() => {
        textSectionsRef.current = textSections;
    }, [textSections]);
    useEffect(() => { configContextRef.current = configContext; }, [configContext]);

    const debounceRef = useRef(null);
    const abortControllerRef = useRef(null);

    /**
     * @param {Object} [textOverride] - optional { index, rawContent } to patch a section's
     *   rawContent before sending, solving the stale-closure problem on typing.
     */
    const recalculateAllPrices = useCallback((textOverride) => {
        // Clear previous debounce
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }
        // Abort previous in-flight requests
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        debounceRef.current = setTimeout(async () => {
            const sections = textSectionsRef.current;
            const config = configContextRef.current;

            const controller = new AbortController();
            abortControllerRef.current = controller;

            priceDispatch({ type: 'setPriceLoading', payload: true });
            priceDispatch({ type: 'resetPrice' });

            const fontErpId = config.selectedFontObject?.font?.[0]?.id_erp;
            const globalFontSizeErpId = config.selectedFontSizeObject?.id_erp;
            const typeErpId =
                config.selectedColorObject?.id !== 102
                    ? 3
                    : config.selectedMaterialObject?.id_erp;

            // Build requests for sections that have content
            const requests = sections.map((section, index) => {
                // Use override content if provided for this section
                const rawContent =
                    textOverride && textOverride.index === index
                        ? textOverride.rawContent
                        : section.rawContent;

                if (!rawContent || rawContent.trim() === '') {
                    return null;
                }

                const fontSizeErpId = section.fontSizeErpId || globalFontSizeErpId;
                const apiPayload = { text: rawContent, fontSizeErpId, fontErpId, typeErpId };

                return priceService
                    .getLetterPrice(apiPayload, controller.signal)
                    .then((result) => {
                        return { index, result };
                    });
            });

            const activeRequests = requests.filter(Boolean);

            try {
                const results = await Promise.allSettled(activeRequests);

                // If aborted, don't dispatch
                if (controller.signal.aborted) return;

                results.forEach((settled) => {
                    if (settled.status === 'fulfilled') {
                        const { index, result } = settled.value;
                        const priceNett = result.price ? parseFloat(result.price).toFixed(2) : '0.00';
                        const priceGross = result.priceGross ? parseFloat(result.priceGross).toFixed(2) : '0.00';
                        priceDispatch({
                            type: 'updatePrice',
                            payload: { index, priceNett, priceGross },
                        });
                    }
                });
            } catch (err) {
                if (err.name !== 'AbortError') {
                    console.error('Price calculation error:', err);
                }
            } finally {
                if (!controller.signal.aborted) {
                    priceDispatch({ type: 'setPriceLoading', payload: false });
                }
            }
        }, 400);
    }, [priceDispatch]);

    return { recalculateAllPrices };
}
